// SPDX-License-Identifier: BUSL-1.1

/**
 * Generic DataTable component built on TanStack Table + shadcn Table.
 *
 * Supports sorting, pagination, and empty states out of the box.
 * Used across all list pages (configs, flags, audit, etc.).
 *
 */

import { useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface DataTableProps<TData, TValue> {
  /** Column definitions following TanStack Table's ColumnDef API. */
  columns: ColumnDef<TData, TValue>[];
  /** Row data to render. */
  data: TData[];
  /** Message shown when data is empty. Defaults to "No results.". */
  emptyMessage?: string;
  /** Number of rows per page. Defaults to 10. */
  pageSize?: number;
  /**
   * Page-size options shown in the footer dropdown. Pass `null` to hide
   * the dropdown entirely (legacy behavior — useful for tables where
   * pagination size is fixed). Defaults to [10, 25, 50, 100].
   */
  pageSizeOptions?: number[] | null;
  /** Callback when a row is clicked. */
  onRowClick?: (row: TData) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage = 'No results.',
  pageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  // Track the user-chosen page size separately from the prop default so the
  // dropdown can override it without losing state across re-renders.
  const [activePageSize, setActivePageSize] = useState(pageSize);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: activePageSize },
    },
  });

  return (
    <div>
      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  return (
                    <TableHead
                      key={header.id}
                      className={canSort ? 'cursor-pointer select-none' : undefined}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {canSort && (
                        <span className="ml-1 inline-block">
                          {{ asc: '\u2191', desc: '\u2193' }[header.column.getIsSorted() as string] ?? '\u2195'}
                        </span>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className={onRowClick ? 'cursor-pointer hover:bg-neutral-800/50' : undefined}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination + page-size dropdown.
          Footer is shown if either (a) there are multiple pages, or
          (b) the dropdown is enabled — so users can still resize a
          single-page table down to e.g. 10 rows. */}
      {(table.getPageCount() > 1 || (pageSizeOptions && pageSizeOptions.length > 0)) && (
        <div className="flex items-center justify-between gap-2 py-4">
          {pageSizeOptions && pageSizeOptions.length > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page</span>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value: string | null) => {
                  if (value === null) return;
                  const next = Number(value);
                  setActivePageSize(next);
                  table.setPageSize(next);
                }}
              >
                <SelectTrigger className="h-8 w-[80px]">
                  <SelectValue placeholder={String(table.getState().pagination.pageSize)} />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((opt) => (
                    <SelectItem key={opt} value={String(opt)}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                · {data.length} total
              </span>
            </div>
          ) : (
            <div />
          )}
          {table.getPageCount() > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export type { DataTableProps };
